/**
 * Shared minimum-price sync for calculator product and initial static price sync for form
 *
 * @param {Object} options
 * @param {function(): string|number} options.getMinimumPrice
 * @param {'curtain'|'roman-blind'|'cushion'|'venetian-blind'} options.mode
 */
(function () {
  'use strict';

  // Checking if the user is choosing measurements options
  const isChoosingMeasurementsOptions = () => {
    const drawer = document.querySelector('calculator-drawer');
    if (!drawer || !drawer.classList.contains('active')) return false;
    if (typeof window.getActiveTrigger !== 'function') return false;

    const windowTrigger = window.getActiveTrigger();
    if (!windowTrigger || !windowTrigger.dataset || !windowTrigger.dataset.target) return false;

    const target = windowTrigger.dataset.target;
    return target === 'Measurements' || target === 'Size';
  }

  let suffixReplayActive = false;
  let lastChangedSelectionHost = null;

  const selectionSuffixByKey = new Map();

  const getSelectionSuffixKey = (calcProp) => {
    if (!calcProp) return '';
    const name = calcProp.name || calcProp.id || '';
    const value = calcProp.type === 'checkbox'
      ? (calcProp.checked ? 'checked' : 'unchecked')
      : (calcProp.value || '');
    return `${name}::${value}`;
  };

  const resolveCalcPropFromHost = (hostEl) => {
    if (!hostEl) return null;
    if (hostEl.matches && hostEl.matches('.calc-prop')) return hostEl;
    return hostEl.querySelector('.calc-prop:checked') || hostEl.querySelector('.calc-prop');
  };

  const resolveOptionHostEl = (calcProp) => {
    if (!calcProp) return null;
    return calcProp.closest('label') || calcProp.parentNode || calcProp;
  };

  const storeSelectionSuffix = (calcProp, suffix) => {
    const key = getSelectionSuffixKey(calcProp);
    if (key) selectionSuffixByKey.set(key, suffix || '');
  };

  const getStoredSelectionSuffix = (calcProp) => {
    const key = getSelectionSuffixKey(calcProp);
    return key ? (selectionSuffixByKey.get(key) || '') : '';
  };

  const hasStoredSelectionSuffix = (calcProp) => {
    const key = getSelectionSuffixKey(calcProp);
    return Boolean(key && selectionSuffixByKey.has(key));
  };

  const getCurrencySymbol = () => {
    return document.querySelector('[data-set-starting-price-wrapper="product-builder"]')
      ?.closest('product-price-starter')
      ?.getAttribute('data-currency-symbol') || '£';
  };

  const formatZeroSelectionSuffix = () => `(${getCurrencySymbol()}0.00)`;

  const normalizeSelectionSuffix = (suffix, options = {}) => {
    const { fallbackZero = false } = options;

    if (suffix) return suffix;

    return fallbackZero ? formatZeroSelectionSuffix() : '';
  };

  const isZeroSelectionSuffix = (suffix) => {
    if (!suffix) return true;

    const amountMatch = String(suffix).match(/(\+|-)?[^\d]*([\d,]+\.\d{2})/);
    if (!amountMatch) return false;

    return Math.abs(parseFloat(amountMatch[2].replace(/,/g, ''))) < 0.005;
  };

  const isNegativeSelectionSuffix = (suffix) => {
    return Boolean(suffix && /^\(-/.test(String(suffix)));
  };

  const isWithinFirstActiveElementSection = (targetEl) => {
    const clone = document.querySelector('#calculator-clone');
    if (!clone || !targetEl) return false;

    const firstActiveSection = clone.querySelector('.element.active');
    return Boolean(firstActiveSection && firstActiveSection.contains(targetEl));
  };

  const shouldHideSelectionSuffix = (targetEl, suffix) => {
    if (isZeroSelectionSuffix(suffix)) return true;
    return isNegativeSelectionSuffix(suffix) && !isWithinFirstActiveElementSection(targetEl);
  };

  const waitForAnimationFrames = (count) => {
    return new Promise((resolve) => {
      const step = (remaining) => {
        if (remaining <= 0) {
          resolve();
          return;
        }
        requestAnimationFrame(() => step(remaining - 1));
      };
      step(count);
    });
  };

  const getActiveDrawerTarget = () => {
    if (typeof window.getActiveTrigger !== 'function') return '';
    const trigger = window.getActiveTrigger();
    return trigger?.dataset?.target || '';
  };

  /** Hardware drawer restores state async (~100ms) before price is final. */
  const waitForDrawerOpenReady = () => {
    const delay = getActiveDrawerTarget() === 'Hardware' ? 150 : 0;

    return new Promise((resolve) => {
      const finish = () => waitForAnimationFrames(2).then(resolve);

      if (delay) setTimeout(finish, delay);
      else finish();
    });
  };

  const waitForReplayDeltaSuffix = () => {
    return new Promise((resolve) => {
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        document.removeEventListener('product-price-starter:delta-suffix', onDeltaSuffix);
        resolve(window.productPriceStarterDeltaSuffix || '');
      };

      const onDeltaSuffix = () => finish();

      document.addEventListener('product-price-starter:delta-suffix', onDeltaSuffix);
      setTimeout(finish, 600);
    });
  };

  /** Click pre-selected option so change → calculatePrice → productPriceStarter sets the suffix. */
  const clickPreselectedOptionForSuffix = (targetEl) => {
    return new Promise((resolve) => {
      const calcProp = targetEl?.querySelector('.calc-prop');
      if (!calcProp) {
        resolve(false);
        return;
      }

      const finish = () => {
        suffixReplayActive = false;
        resolve(true);
      };

      const runAfterFrames = (frameCount, callback) => {
        if (frameCount <= 0) {
          callback();
          return;
        }
        requestAnimationFrame(() => runAfterFrames(frameCount - 1, callback));
      };

      suffixReplayActive = true;

      const startReplay = () => {
        if (calcProp.type === 'radio') {
          const siblings = Array.from(document.querySelectorAll(`input[name="${CSS.escape(calcProp.name)}"]`))
            .filter((input) => !input.disabled);
          const selected = siblings.find((input) => input.checked);
          const alternate = siblings.find((input) => input !== selected);

          if (!selected || !alternate) {
            finish();
            return;
          }

          const selectRadio = (input) => {
            siblings.forEach((el) => { el.checked = el === input; });
            input.dispatchEvent(new Event('change', { bubbles: true }));
          };

          selectRadio(alternate);
          runAfterFrames(2, () => {
            const suffixPromise = waitForReplayDeltaSuffix();
            selectRadio(selected);
            suffixPromise.then(() => runAfterFrames(2, finish));
          });
          return;
        }

        if (calcProp.tagName === 'SELECT') {
          const alternate = Array.from(calcProp.options).find((option) => option.value && option.value !== calcProp.value);
          if (!alternate) {
            finish();
            return;
          }

          const savedValue = calcProp.value;
          calcProp.value = alternate.value;
          calcProp.dispatchEvent(new Event('change', { bubbles: true }));

          runAfterFrames(2, () => {
            const suffixPromise = waitForReplayDeltaSuffix();
            calcProp.value = savedValue;
            calcProp.dispatchEvent(new Event('change', { bubbles: true }));
            suffixPromise.then(() => runAfterFrames(2, finish));
          });
          return;
        }

        if (calcProp.type === 'checkbox' && calcProp.checked) {
          calcProp.checked = false;
          calcProp.dispatchEvent(new Event('change', { bubbles: true }));

          runAfterFrames(2, () => {
            const suffixPromise = waitForReplayDeltaSuffix();
            calcProp.checked = true;
            calcProp.dispatchEvent(new Event('change', { bubbles: true }));
            suffixPromise.then(() => runAfterFrames(2, finish));
          });
          return;
        }

        finish();
      };

      runAfterFrames(2, startReplay);
    });
  };

  const applySelectionCostSuffixToElement = (targetEl, suffixText) => {
    if (!targetEl) return;

    const calcProp = resolveCalcPropFromHost(targetEl);
    if (!calcProp) return;

    const hasExplicitSuffix = suffixText != null;

    if (hasExplicitSuffix) {
      storeSelectionSuffix(calcProp, suffixText);
    }

    const price = hasExplicitSuffix ? suffixText : getStoredSelectionSuffix(calcProp);

    if (!hasExplicitSuffix && !hasStoredSelectionSuffix(calcProp)) {
      return;
    }

    if (!price || shouldHideSelectionSuffix(targetEl, price)) {
      return;
    }

    if (targetEl.querySelector('input[type="checkbox"].calc-prop')?.checked === false) {
      targetEl.classList.remove('selected-option');
    }
  };

  /**
   * Update suffix on the changed option; other active sections keep their stored values.
   */
  const updateSelectionCostSuffixes = (changedHostEl) => {
    const clone = document.querySelector('#calculator-clone');
    if (!clone) return;

    clone.querySelectorAll('.element.active .selected-option').forEach((el) => {
      if (el === changedHostEl) {
        applySelectionCostSuffixToElement(el, normalizeSelectionSuffix(window.productPriceStarterDeltaSuffix));
      } else {
        applySelectionCostSuffixToElement(el);
      }
    });
  };

  // all the different picker class/attribute types in the calculator
  const findCalcPropOptionGroup = (optionRoot) => {
    if (!optionRoot) return null;
    return optionRoot.closest(
      [
        '.swatch-picker',
        '.lining-type-picker',
        '.curtain-type-picker',
        '.curtain-poles-picker',
        '.pole-material-picker',
        '.finish-picker',
        '.finial-picker',
        '.pole-size-picker',
        '.track-mounting-picker',
        '.side-picker',
        '.size-type-picker',
        '.additional-options-type-picker',
        '[data-measurement-preset-group]',
      ].join(', ')
    );
  };

  const clearSelectedOptionInGroup = (calcPropEl) => {
    if (!calcPropEl || !calcPropEl.parentNode) return;
    const optionRoot = calcPropEl.parentNode;
    const group = findCalcPropOptionGroup(optionRoot) || optionRoot.parentNode;
    if (group) {
      Array.from(group.children).forEach((el) => {
        el.classList.remove('selected-option');
      });
    }
  };

  const syncSelectedOptionWithSiblings = (calcPropEl) => {
    if (!calcPropEl || !calcPropEl.parentNode) return null;
    const optionRoot = calcPropEl.parentNode;
    const group = findCalcPropOptionGroup(optionRoot) || optionRoot.parentNode;

    if (group) {
      Array.from(group.children).forEach((el) => {
        el.classList.remove('selected-option');
        document.dispatchEvent(new CustomEvent('builder-selected-option:false'));
      });
    }

    optionRoot.classList.add('selected-option');
    document.dispatchEvent(new CustomEvent('builder-selected-option:true'));

    return optionRoot;
  };

  /**
   * Apply selected-option for every chosen control in visible calculator sections.
   * Clears each option group for every calc-prop
   */
  const syncSelectedOptionsInActiveSections = () => {
    const clone = document.querySelector('#calculator-clone');
    if (!clone) return;

    let sections = Array.from(clone.querySelectorAll('.element.active'));

    if (!sections.length) {
      sections = [clone];
    }

    sections.forEach((section) => {
      section.querySelectorAll('input.calc-prop, select.calc-prop').forEach((prop) => {
        clearSelectedOptionInGroup(prop);
      });
    });

    sections.forEach((section) => {
      section.querySelectorAll('input.calc-prop:checked').forEach((input) => {
        syncSelectedOptionWithSiblings(input);
      });
      section.querySelectorAll('select.calc-prop').forEach((select) => {
        syncSelectedOptionWithSiblings(select);
      });
    });
  };

  const resolveCalcPropFromClick = (target) => {
    if (!target || !target.closest) return null;
    let prop = target.closest('.calc-prop');
    if (prop) return prop;

    const host = target.closest('label.swatch-label, label.curtain-option, label.lining-option, label');

    if (host) {
      prop = host.querySelector('.calc-prop');
    }
    
    return prop || null;
  };

  const collectAppendTargetsForDrawerOpen = () => {
    const sections = document.querySelectorAll('#calculator-clone .element.active');
    const seen = new Set();
    const targets = [];

    sections.forEach((section) => {
      section.querySelectorAll('input.calc-prop:checked, select.calc-prop').forEach((prop) => {
        const parent = prop.parentNode;
        if (parent && !seen.has(parent)) {
          seen.add(parent);
          targets.push(parent);
        }
      });
    });

    return targets;
  };

  let selectionSuffixTimeoutId = null;
  let selectionSuffixTimeoutResolve = null;
  let selectionSuffixUpdatedForChange = false;
  let scheduledSelectionPriceEvent = null;
  let selectionPriceUpdateQueued = false;

  const scheduleSetCalculatorSelectionPrices = (event) => {
    scheduledSelectionPriceEvent = event;

    if (selectionPriceUpdateQueued) return;

    selectionPriceUpdateQueued = true;

    queueMicrotask(() => {
      selectionPriceUpdateQueued = false;
      const evt = scheduledSelectionPriceEvent;
      scheduledSelectionPriceEvent = null;

      if (evt) setCalculatorSelectionPrices(evt);
    });
  };

  const setCalculatorSelectionPrices = (event) => {
    let appendTargets = [];
    const isDrawerOpen = Boolean(event && event.drawerOpen);

    if (!isDrawerOpen && event && event.target) {
      const propChanged = resolveCalcPropFromClick(event.target);
      if (propChanged) {
        lastChangedSelectionHost = resolveOptionHostEl(propChanged);
      }
    }

    syncSelectedOptionsInActiveSections();

    if (isDrawerOpen) {
      appendTargets = collectAppendTargetsForDrawerOpen();
    } else if (event && event.target && event.target.closest) {
      if (event.target.closest('.active')) {
        const propClicked = resolveCalcPropFromClick(event.target);
        const host = propClicked ? resolveOptionHostEl(propClicked) : null;
        if (host) appendTargets = [host];
      }
    }

    if (!appendTargets.length || isChoosingMeasurementsOptions()) return Promise.resolve(false);

    selectionSuffixUpdatedForChange = false;

    if (selectionSuffixTimeoutId !== null) {
      clearTimeout(selectionSuffixTimeoutId);
      selectionSuffixTimeoutId = null;

      if (typeof selectionSuffixTimeoutResolve === 'function') {
        // Resolve previous waiter when superseded by a new selection.
        selectionSuffixTimeoutResolve(false);
        selectionSuffixTimeoutResolve = null;
      }
    }

    const appendedTargets = appendTargets.slice();

    if (isDrawerOpen && appendedTargets.length) {
      return waitForDrawerOpenReady().then(() => {
        syncSelectedOptionsInActiveSections();
        const drawerTargets = collectAppendTargetsForDrawerOpen();

        const replayTargetsSequentially = (index, targets) => {
          if (index >= targets.length) return Promise.resolve(true);

          const targetEl = targets[index];

          return clickPreselectedOptionForSuffix(targetEl)
            .then(() => waitForAnimationFrames(2))
            .then(() => {
              const suffix = window.productPriceStarterDeltaSuffix || '';
              if (suffix) {
                applySelectionCostSuffixToElement(
                  targetEl,
                  normalizeSelectionSuffix(suffix, { fallbackZero: false })
                );
              }
              return replayTargetsSequentially(index + 1, targets);
            });
        };

        return replayTargetsSequentially(0, drawerTargets.length ? drawerTargets : appendedTargets);
      });
    }

    const changedHost = appendedTargets[0] || lastChangedSelectionHost;

    return new Promise((resolve) => {
      selectionSuffixTimeoutResolve = resolve;

      selectionSuffixTimeoutId = setTimeout(() => {
        selectionSuffixTimeoutId = null;
        selectionSuffixTimeoutResolve = null;

        if (!selectionSuffixUpdatedForChange) {
          updateSelectionCostSuffixes(changedHost);
        }

        resolve(true);
      }, 800);
    });
  }

  window.setCalculatorSelectionPrices = setCalculatorSelectionPrices;
  window.updateCalculatorSelectionCostSuffixes = updateSelectionCostSuffixes;

  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      const priceItemRegular = document.querySelector('.product__info-wrapper .price-item.price-item--regular');

      priceItemRegular.classList.remove('loading-state');
    }, 500);
  });

  document.addEventListener('product-price-starter:delta-suffix', () => {
    if (suffixReplayActive) return;

    selectionSuffixUpdatedForChange = true;

    const changedHost = lastChangedSelectionHost;

    if (changedHost) {
      const calcProp = resolveCalcPropFromHost(changedHost);
      if (calcProp) {
        storeSelectionSuffix(calcProp, normalizeSelectionSuffix(window.productPriceStarterDeltaSuffix));
      }
      lastChangedSelectionHost = null;
    }

    updateSelectionCostSuffixes(changedHost);

    if (selectionSuffixTimeoutId !== null) {
      clearTimeout(selectionSuffixTimeoutId);
      selectionSuffixTimeoutId = null;

      if (typeof selectionSuffixTimeoutResolve === 'function') {
        selectionSuffixTimeoutResolve(true);
        selectionSuffixTimeoutResolve = null;
      }
    }
  });

  document.addEventListener('change', (event) => {
    if (suffixReplayActive) return;
    if (!event.target?.classList?.contains('calc-prop')) return;

    scheduleSetCalculatorSelectionPrices(event);
  });
})();
